import { useState, useSyncExternalStore, type ReactNode } from 'react'
import type { PromptToolStore } from '../../data/use-prompt-tool-store.ts'
import { EngineModuleCard } from '../../ui/EngineModuleCard.tsx'
import { MenuSelect } from '../../ui/MenuSelect.tsx'
import styles from '../../ui/controls.module.css'
import { buildEffortOptions, buildModelOptions, modelChoiceValue, parseModelChoice } from './model-options.ts'
/** 模型路由模块卡（主会话与本地子代理分别走当前模块请求覆盖）：
 *  主对话/子代理共用同一配置源（缺省继承宿主默认）；模型路由与人设按作用域完全分离
 *  （main=主对话模型、subagent=子代理模型，参数各自独立）。 */
export function ModelRouteModuleCard(props: { store: PromptToolStore; scope: 'main' | 'subagent' }): ReactNode {
  const { store } = props
  const fields = store.fields
  const disabled = !fields.writePreset || store.moduleFacts?.editable !== true
  const host = store.hostDefaultModel
  // 当前会话模型选择（官方 session 投影 + selectModel 通道）：会话切换即换显，
  // 宿主侧切换经投影帧实时回流；投影缺省时回退宿主默认（对齐官方目录 current 语义）。
  const sessionFace = store.api.sessionModel
  const sessionView = useSyncExternalStore(sessionFace.subscribe, sessionFace.snapshot)
  const [selecting, setSelecting] = useState(false)
  const sessionProvider = sessionView.selection?.provider ?? host?.provider ?? ''
  const sessionModelName = sessionView.selection?.model ?? host?.model ?? ''
  const sessionEffort = sessionView.selection?.reasoningEffort ?? host?.reasoningEffort ?? ''
  // 宿主默认模型回显：插件参数未设置（空 = 继承宿主）时，下拉可见宿主当前
  // agent-default-model 的可选项（模型目录查询失败/未公布时也能选择与回显）。
  const provider = props.scope === 'main' ? fields.modelProvider : fields.subagentModelProvider
  const modelName = props.scope === 'main' ? fields.modelName : fields.subagentModelName
  // 宿主默认思维程度回显（agent-default-model settings reasoningEffort；官方档位同源）。
  const hostEffort = host?.reasoningEffort !== undefined && host.reasoningEffort.length > 0
    ? host.reasoningEffort
    : undefined
  const active = provider.length > 0 && modelName.length > 0
  const modelOptions = buildModelOptions(store.modelCatalog, [
    ...(provider.length > 0 && modelName.length > 0 ? [{ provider, model: modelName }] : []),
    ...(host?.provider !== undefined && host.provider.length > 0 && host.model !== undefined && host.model.length > 0
      ? [{ provider: host.provider, model: host.model }]
      : []),
  ])
  const sessionModelOptions = buildModelOptions(store.modelCatalog, [
    ...(sessionProvider.length > 0 && sessionModelName.length > 0
      ? [{ provider: sessionProvider, model: sessionModelName }]
      : []),
    ...(host?.provider !== undefined && host.provider.length > 0 && host.model !== undefined && host.model.length > 0
      ? [{ provider: host.provider, model: host.model }]
      : []),
  ]).filter((option) => option.value.length > 0)
  const reasoningEffort = props.scope === 'main' ? fields.modelReasoningEffort : fields.subagentReasoningEffort
  // 思维程度档位来自官方元数据（store.modelReasoning 按 provider/model 路由查得）：
  // 未查到或该路由不提供推理档位时不展示固定列表，存量值仍保留回显（M-12）。
  const sessionReasoning = store.modelReasoning[modelChoiceValue(sessionProvider, sessionModelName)]
  const presetReasoning = store.modelReasoning[modelChoiceValue(provider, modelName)]
  const sessionEffortOptions = buildEffortOptions(sessionReasoning, sessionEffort)
  const presetEffortOptions = buildEffortOptions(presetReasoning, reasoningEffort)
  const temperature = props.scope === 'main' ? fields.modelTemperature : fields.subagentTemperature
  const maxTokens = props.scope === 'main' ? fields.modelMaxTokens : fields.subagentMaxTokens
  const patchModelParam = (key: 'modelReasoningEffort' | 'modelTemperature' | 'modelMaxTokens' | 'subagentReasoningEffort' | 'subagentTemperature' | 'subagentMaxTokens', value: string): void => {
    if (disabled) return
    store.patch({ [key]: value } as Partial<typeof fields>)
    void store.persistParamOverrides()
  }
  // 会话级切换：官方 selectModel 需要完整 provider+model；单项改动与其余当前值合并提交。
  const applySessionSelection = (patch: { provider?: string; model?: string; reasoningEffort?: string }): void => {
    const nextProvider = patch.provider ?? sessionProvider
    const nextModel = patch.model ?? sessionModelName
    const nextEffort = patch.reasoningEffort ?? sessionEffort
    if (nextProvider.length === 0 || nextModel.length === 0) {
      store.showNotice('error', '会话模型切换需要服务商与模型名：当前会话尚无完整选择')
      return
    }
    setSelecting(true)
    void sessionFace.select({ provider: nextProvider, model: nextModel, ...(nextEffort.length > 0 ? { reasoningEffort: nextEffort } : {}) })
      .then(() => {
        store.showNotice('ok', '已切换当前会话模型选择（宿主默认同步更新）')
        // 宿主默认已随 selectModel 更新：刷新桥回显（hostDefaultModel）。
        void store.load()
      })
      .catch((error: unknown) => {
        store.showNotice('error', '会话模型切换失败：' + (error instanceof Error ? error.message : String(error)))
      })
      .finally(() => setSelecting(false))
  }
  const scopeMeta = props.scope === 'main'
    ? { title: '模型路由', idle: '未设置：展开选择模型（留空 = 继承当前会话）', active: '固定模型路由已设置（仅当前模块请求）' }
    : { title: '子代理请求模型', idle: '未设置：展开选择模型（留空 = 继承子代理当前请求）', active: '本地子代理请求路由已设置' }
  // 主对话卡片：宿主默认模型名回显（子代理默认继承主会话，不回显宿主）。
  const idleMeta = props.scope === 'main' && host?.model !== undefined && host.model.length > 0
    ? `未设置：展开选择模型（当前继承宿主默认 ${host.model}）`
    : scopeMeta.idle
  return (
    <EngineModuleCard name={scopeMeta.title} meta={active ? scopeMeta.active : idleMeta}
      defaultExpanded={store.editorDrafts?.expanded.get(`${fields.presetTemplate}:model:${props.scope}`)}
      onExpandedChange={(value) => store.editorDrafts?.expanded.set(`${fields.presetTemplate}:model:${props.scope}`, value)}>
      {props.scope === 'main' && (
        <div className={styles.settingRowStack}>
          <span className={styles.settingCopy}>
            <strong>当前会话</strong>
            <small>{sessionView.sessionId === undefined
              ? '无活动会话：打开会话后此处显示其模型选择并可切换。'
              : sessionView.selectable
                ? '与官方模型选择器同源（session.selectModel）：切换即对当前会话生效，并保存为宿主新会话默认。下方模块参数非空时按请求覆盖会话选择。'
                : '子代理会话不支持会话级切换；下方模块参数可覆盖本地子代理请求。'}</small>
          </span>
          <div className={styles.sessionModelRow}>
            <MenuSelect
              className={styles.configInput}
              compact
              ariaLabel="会话模型"
              value={sessionProvider.length > 0 && sessionModelName.length > 0 ? modelChoiceValue(sessionProvider, sessionModelName) : ''}
              disabled={!sessionView.selectable || selecting}
              options={sessionModelOptions}
              onChange={(value) => {
                const selection = parseModelChoice(value)
                if (selection !== undefined) applySessionSelection(selection)
              }}
            />
            <MenuSelect
              className={styles.configInput}
              compact
              ariaLabel="会话思维程度"
              value={sessionEffort}
              disabled={!sessionView.selectable || selecting}
              options={sessionEffortOptions}
              onChange={(reasoningEffort) => applySessionSelection({ reasoningEffort })}
            />
          </div>
        </div>
      )}
      <div className={styles.settingRowStack}>
        <span className={styles.settingCopy}>
          <strong>模块模型</strong>
          <small>{props.scope === 'main'
            ? `选择模型后保存到当前模块；思维程度档位按所选模型声明展示，不改写宿主全局默认模型。留空 = 继承宿主默认${hostEffort !== undefined ? `（思维程度当前为 ${hostEffort}）` : ''}。`
            : '只覆盖本地子代理的后续模型请求，不改写普通官方委派的创建参数；外部子代理运行时不适用。留空 = 继承当前请求。思维程度档位按所选模型声明展示。'}</small>
        </span>
        <div className={styles.sessionModelRow}>
          <MenuSelect
            className={styles.configInput}
            compact
            ariaLabel="模块模型"
            value={active ? modelChoiceValue(provider, modelName) : ''}
            disabled={disabled}
            options={modelOptions}
            onChange={(value) => {
              const selection = parseModelChoice(value)
              const patch = selection === undefined
                ? (props.scope === 'main' ? { modelProvider: '', modelName: '' } : { subagentModelProvider: '', subagentModelName: '' })
                : (props.scope === 'main'
                  ? { modelProvider: selection.provider, modelName: selection.model }
                  : { subagentModelProvider: selection.provider, subagentModelName: selection.model })
              store.patch(patch)
              void store.persistParamOverrides()
            }}
          />
          <MenuSelect
            className={styles.configInput}
            compact
            ariaLabel="思维程度"
            value={reasoningEffort}
            disabled={disabled}
            options={presetEffortOptions}
            onChange={(value) => patchModelParam(
              props.scope === 'main' ? 'modelReasoningEffort' : 'subagentReasoningEffort',
              value,
            )}
          />
        </div>
      </div>
      <div className={styles.settingRowStack}>
        <span className={styles.settingCopy}>
          <strong>采样参数</strong>
          <small>temperature（数字 0–2）与 maxTokens（正整数）经 agent-request patch 生效；留空 = 不设置（模型默认）。失焦保存。</small>
        </span>
        <div className={styles.sessionModelRow}>
          <input
            className={styles.configInput}
            type="number"
            min={0}
            max={2}
            step={0.1}
            aria-label="采样温度"
            value={temperature}
            disabled={disabled}
            placeholder="温度（不设置）"
            onChange={(event) => patchModelParam(
              props.scope === 'main' ? 'modelTemperature' : 'subagentTemperature',
              event.target.value,
            )}
            onBlur={() => { if (!disabled) void store.persistParamOverrides() }}
          />
          <input
            className={styles.configInput}
            type="number"
            min={1}
            step={1}
            aria-label="输出上限"
            value={maxTokens}
            disabled={disabled}
            placeholder="输出上限（不设置）"
            onChange={(event) => patchModelParam(
              props.scope === 'main' ? 'modelMaxTokens' : 'subagentMaxTokens',
              event.target.value,
            )}
            onBlur={() => { if (!disabled) void store.persistParamOverrides() }}
          />
        </div>
      </div>
    </EngineModuleCard>
  )
}
